import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { ZoomableImage } from "@/components/ZoomableImage";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Users, ScrollText, Send, Mail, Phone, MapPin, Youtube, Swords, Loader2, Plus, X, ChevronRight, FileText, BarChart3, Download, CalendarDays } from "lucide-react";
import { BraggersStatsTable } from "@/components/BraggersStatsTable";
import { BraggersPlayerCard } from "@/components/BraggersPlayerCard";
import { API } from "@/lib/api";

const C = {
  bg: "#060807", surface: "#0E1310", elevated: "#151C18",
  gold: "#D4AF37", goldLight: "#F2E3A0", green: "#1D5C34", greenDark: "#123B21",
  silver: "#8A8D91", border: "#2A302C", text: "#F3F4F6", sub: "#9CA3AF",
};
const SERIF = "'Cormorant Garamond', serif";

const SECTIONS = [
  { id: "current", label: "Current Match" },
  { id: "challengers", label: "Challengers" },
  { id: "champions", label: "Champions" },
  { id: "stats", label: "Player Stats" },
  { id: "rules", label: "Rules" },
  { id: "submit", label: "Submit a Challenge" },
  { id: "contact", label: "Contact" },
];

const ytId = (url) => {
  const m = String(url || "").match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
};

function SectionTitle({ icon: Icon, overline, children }) {
  return (
    <div className="mb-10">
      <p className="text-xs tracking-[0.22em] uppercase font-bold mb-3 flex items-center gap-2" style={{ color: C.silver }}>
        {Icon && <Icon className="h-4 w-4" style={{ color: C.gold }} />} {overline}
      </p>
      <h2 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ fontFamily: SERIF, color: C.text }}>{children}</h2>
    </div>
  );
}

function emptyPlayer() { return { name: "", fargo: "", robustness: "" }; }

export default function BraggersCup() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [statPlayer, setStatPlayer] = useState(null);
  const [active, setActive] = useState("current");
  const [form, setForm] = useState({ contact_name: "", phone: "", email: "", home_location: "", comment: "", company: "" });
  const [players, setPlayers] = useState([emptyPlayer(), emptyPlayer(), emptyPlayer(), emptyPlayer(), emptyPlayer()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/braggers").then((r) => setData(r.data)).catch(() => {});
    api.get("/braggers/stats").then((r) => setStats(r.data)).catch(() => setStats({ datasets: [] }));
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY + 160;
      let cur = "current";
      for (const s of SECTIONS) {
        const el = document.getElementById(`bc-${s.id}`);
        if (el && el.offsetTop <= y) cur = s.id;
      }
      setActive(cur);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(`bc-${id}`);
    if (el) window.scrollTo({ top: el.offsetTop - 130, behavior: "smooth" });
  };

  const setPlayer = (i, k, v) => setPlayers((p) => p.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Name, phone and email are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/braggers/challenge", { ...form, players: players.filter((p) => p.name.trim()) });
      toast.success("Challenge submitted! We'll be in touch to set a match date.");
      setForm({ contact_name: "", phone: "", email: "", home_location: "", comment: "", company: "" });
      setPlayers([emptyPlayer(), emptyPlayer(), emptyPlayer(), emptyPlayer(), emptyPlayer()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const d = data || {};
  const challengers = d.challengers || [];
  const champions = d.champions || [];
  const rules = d.rules || [];
  const inputCls = "w-full rounded-sm px-4 py-3 text-sm outline-none transition-colors";
  const pInput = "rounded-sm px-4 py-3 text-sm outline-none transition-colors";
  const inputStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text };

  return (
    <Layout>
      <Seo title="Bragger's Cup Pool Team Challenge | Eastern Ontario | Quinte BCA" description="Follow the Quinte BCA Bragger's Cup team challenge, including past matches, team rosters, Fargo ratings, scoresheets and match replays." path="/braggers" />
      <div style={{ background: C.bg, color: C.text }} data-testid="braggers-page">
        {/* HERO */}
        <section className="relative overflow-hidden" style={{ background: `radial-gradient(circle at 70% 20%, ${C.greenDark}55, ${C.bg} 60%)` }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 sm:py-28 flex flex-col lg:flex-row items-center gap-12">
            {d.logo && (
              <img src={fileUrl(d.logo)} alt="Bragger's Cup crest" data-testid="braggers-hero-logo"
                className="h-48 sm:h-64 w-auto object-contain drop-shadow-[0_0_45px_rgba(212,175,55,0.4)]" />
            )}
            <div className="text-center lg:text-left">
              <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4" style={{ color: C.gold }}>Pool Team Challenge Series</p>
              <h1 className="text-5xl sm:text-7xl font-semibold tracking-tight leading-none mb-6" style={{ fontFamily: SERIF, color: C.goldLight }}>
                The Bragger's Cup
              </h1>
              <p className="text-base sm:text-lg leading-relaxed max-w-2xl" style={{ color: C.sub }}>{d.intro}</p>
              <button onClick={() => scrollTo("submit")} data-testid="braggers-hero-cta"
                className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 font-bold rounded-sm transition-colors"
                style={{ background: C.gold, color: C.bg }}>
                <Swords className="h-5 w-5" /> Issue a Challenge
              </button>
            </div>
          </div>
        </section>

        {/* STICKY SUB-NAV */}
        <nav className="sticky top-0 z-40 backdrop-blur-xl border-y" style={{ background: `${C.bg}cc`, borderColor: C.border }} data-testid="braggers-subnav">
          <div className="max-w-7xl mx-auto px-4 lg:px-12 flex gap-1 overflow-x-auto no-scrollbar">
            {SECTIONS.map((s) => (
              <button key={s.id} onClick={() => scrollTo(s.id)} data-testid={`braggers-nav-${s.id}`}
                className="px-4 py-4 text-sm whitespace-nowrap border-b-2 transition-colors"
                style={{ borderColor: active === s.id ? C.gold : "transparent", color: active === s.id ? C.gold : C.sub }}>
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        {/* CURRENT MATCH */}
        <section id="bc-current" className="max-w-7xl mx-auto px-6 lg:px-12 py-20 sm:py-28">
          <SectionTitle icon={Swords} overline="On the Table">Current Match</SectionTitle>
          {d.current_match_poster ? (
            <div className="flex flex-col items-center">
              <div className="w-full max-w-2xl rounded-sm overflow-hidden border" style={{ borderColor: C.border, background: C.surface }}>
                <ZoomableImage src={fileUrl(d.current_match_poster)} alt="Current match poster" testId="braggers-current-poster"
                  className="w-full h-auto object-contain max-h-[80vh]" />
              </div>
              {d.current_match_caption && <p className="mt-5 text-center max-w-2xl" style={{ color: C.sub }}>{d.current_match_caption}</p>}
            </div>
          ) : (
            <p className="text-lg" style={{ color: C.sub }}>No match is currently scheduled. Check back soon — or issue your own challenge below.</p>
          )}
        </section>

        {/* CHALLENGERS */}
        <section id="bc-challengers" className="py-20 sm:py-28" style={{ background: C.surface }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <SectionTitle icon={Users} overline="In the Queue">Challengers Signed Up</SectionTitle>
            {challengers.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {challengers.map((c, i) => (
                  <div key={i} data-testid={`braggers-challenger-${i}`} className="p-6 rounded-sm border relative overflow-hidden" style={{ background: C.elevated, borderColor: C.border }}>
                    <div className="absolute top-0 left-0 w-1 h-full" style={{ background: C.gold }} />
                    <h3 className="text-2xl font-medium mb-1" style={{ fontFamily: SERIF, color: C.goldLight }}>{c.team_name || "TBD"}</h3>
                    {c.captain && <p className="text-sm" style={{ color: C.sub }}>Captain: {c.captain}</p>}
                    {c.home_location && <p className="text-sm flex items-center gap-1.5 mt-1" style={{ color: C.sub }}><MapPin className="h-3.5 w-3.5" /> {c.home_location}</p>}
                    {c.date && <p className="text-sm mt-2 font-semibold" style={{ color: C.gold }}>{c.date}</p>}
                    {c.note && <p className="text-sm mt-2" style={{ color: C.sub }}>{c.note}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-lg" style={{ color: C.sub }}>No challengers in the queue yet. Be the first to step up!</p>
            )}
          </div>
        </section>

        {/* CHAMPIONS */}
        <section id="bc-champions" className="max-w-7xl mx-auto px-6 lg:px-12 py-20 sm:py-28">
          <SectionTitle icon={Trophy} overline="Hall of Fame">Champions</SectionTitle>
          {champions.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="braggers-champions-grid">
              {champions.map((ch, i) => {
                const winner = ch.winning_team || ch.title || "Champions";
                const vs = ch.winning_team || ch.challenger_team ? `${ch.winning_team || "Champions"} vs ${ch.challenger_team || "Challengers"}` : (ch.title || "Champions");
                const to = ch.slug ? `/braggers/${ch.slug}` : null;
                const Card = (
                  <>
                    <div className="relative aspect-video overflow-hidden" style={{ background: C.elevated }}>
                      {ch.team_photo ? (
                        <img src={fileUrl(ch.team_photo)} alt={`${winner} — Bragger's Cup ${ch.edition || ""}`} loading="lazy" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Trophy className="h-10 w-10" style={{ color: C.gold }} /></div>
                      )}
                      {ch.edition && <span className="absolute top-2 left-2 rounded-sm px-2 py-0.5 text-xs font-bold" style={{ background: C.gold, color: C.bg }}>#{ch.edition}</span>}
                    </div>
                    <div className="p-5">
                      <h3 className="text-xl font-medium leading-snug" style={{ fontFamily: SERIF, color: C.goldLight }}>{vs}</h3>
                      {ch.date && <p className="text-sm mt-1" style={{ color: C.sub }}>{ch.date}</p>}
                      {ch.winning_team && <p className="text-sm mt-2 inline-flex items-center gap-1.5" style={{ color: C.gold }}><Trophy className="h-3.5 w-3.5" /> {ch.winning_team}</p>}
                      {to && <p className="mt-3 text-sm font-semibold inline-flex items-center gap-1" style={{ color: C.gold }}>View Match <ChevronRight className="h-4 w-4" /></p>}
                    </div>
                  </>
                );
                return to ? (
                  <Link key={i} to={to} data-testid={`braggers-champion-${i}`} className="block rounded-sm border overflow-hidden transition-transform hover:-translate-y-0.5" style={{ background: C.surface, borderColor: C.border }}>{Card}</Link>
                ) : (
                  <div key={i} data-testid={`braggers-champion-${i}`} className="rounded-sm border overflow-hidden" style={{ background: C.surface, borderColor: C.border }}>{Card}</div>
                );
              })}
            </div>
          ) : (
            <p className="text-lg" style={{ color: C.sub }}>The first champion is yet to be crowned. History starts here.</p>
          )}
        </section>

        {/* PLAYER STATS */}
        <section id="bc-stats" className="py-20 sm:py-28 border-y" style={{ background: "#0A0F0C", borderColor: C.border }} data-testid="braggers-stats-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-x-6">
              <SectionTitle icon={BarChart3} overline="By the Numbers">Player Stats</SectionTitle>
              {stats?.datasets?.length > 0 && (
                <a href={`${API}/braggers/stats/download`} download data-testid="braggers-stats-download"
                  className="mb-10 inline-flex items-center gap-2 self-start sm:self-auto px-5 py-3 rounded-sm font-semibold border transition-colors hover:bg-[#D4AF37] hover:text-[#060807]"
                  style={{ borderColor: C.gold, color: C.gold }}>
                  <Download className="h-4 w-4" /> Download spreadsheet
                </a>
              )}
            </div>
            {stats?.note && (
              <p className="-mt-6 mb-10 inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-sm border" data-testid="braggers-stats-note"
                style={{ color: C.goldLight, borderColor: `${C.gold}66`, background: `${C.gold}12` }}>
                <CalendarDays className="h-4 w-4 shrink-0" style={{ color: C.gold }} /> {stats.note}
              </p>
            )}
            {stats === null ? (
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: C.gold }} />
            ) : stats.datasets?.length ? (
              <div className="space-y-16">
                {stats.datasets.map((ds) => (
                  <BraggersStatsTable key={ds.dataset} dataset={ds} C={C} testid={`braggers-stats-${ds.dataset}`} onPlayer={setStatPlayer} />
                ))}
                <BraggersPlayerCard name={statPlayer} datasets={stats.datasets} C={C} onClose={() => setStatPlayer(null)} />
              </div>
            ) : (
              <div className="p-10 rounded-sm border text-center" style={{ background: C.surface, borderColor: C.border }} data-testid="braggers-stats-coming-soon">
                <BarChart3 className="h-10 w-10 mx-auto mb-4" style={{ color: C.gold }} />
                <p className="text-2xl font-medium mb-2" style={{ fontFamily: SERIF, color: C.goldLight }}>Data coming soon</p>
                <p className="text-sm" style={{ color: C.sub }}>Player analysis and breakdown stats will appear here once they're published.</p>
              </div>
            )}
          </div>
        </section>

        {/* RULES */}
        <section id="bc-rules" className="py-20 sm:py-28" style={{ background: C.surface }}>
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <SectionTitle icon={ScrollText} overline="The Code">Guidelines & Rules</SectionTitle>
            {d.rules_pdf && (
              <a
                href={fileUrl(d.rules_pdf)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="braggers-rules-pdf-link"
                className="inline-flex items-center gap-2 mb-8 px-5 py-3 rounded-sm font-semibold border transition-colors hover:opacity-90"
                style={{ borderColor: C.gold, color: C.gold, background: C.elevated }}
              >
                <FileText className="h-4 w-4" /> Complete Rules / Format (PDF)
              </a>
            )}
            <ol className="space-y-4" data-testid="braggers-rules">
              {rules.map((r, i) => (
                <li key={i} className="flex gap-4 p-5 rounded-sm border" style={{ background: C.elevated, borderColor: C.border }}>
                  <span className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: C.gold, color: C.bg, fontFamily: SERIF }}>{i + 1}</span>
                  <span className="leading-relaxed pt-1" style={{ color: C.text }}>{r}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* SUBMIT */}
        <section id="bc-submit" className="max-w-3xl mx-auto px-6 lg:px-12 py-20 sm:py-28">
          <SectionTitle icon={Send} overline="Step Up">Submit a Challenge</SectionTitle>
          <form onSubmit={submit} className="space-y-5" data-testid="braggers-challenge-form">
            <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="hidden" aria-hidden />
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Contact name *"><input data-testid="bc-contact-name" className={inputCls} style={inputStyle} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
              <Field label="Home location"><input data-testid="bc-home" className={inputCls} style={inputStyle} value={form.home_location} onChange={(e) => setForm({ ...form, home_location: e.target.value })} placeholder="e.g. Stix & Stones, Trenton, Ontario" /></Field>
              <Field label="Phone *"><input data-testid="bc-phone" className={inputCls} style={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
              <Field label="Email *"><input data-testid="bc-email" type="email" className={inputCls} style={inputStyle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold mb-3" style={{ color: C.silver }}>Your 5 Players — Fargo & Robustness</p>
              <div className="space-y-3">
                {players.map((p, i) => (
                  <div key={i} className="flex gap-3">
                    <input data-testid={`bc-player-name-${i}`} className={pInput + " flex-1 min-w-0"} style={inputStyle} placeholder={`Player ${i + 1} name`} value={p.name} onChange={(e) => setPlayer(i, "name", e.target.value)} />
                    <input data-testid={`bc-player-fargo-${i}`} className={pInput + " w-24 shrink-0"} style={inputStyle} placeholder="Fargo" value={p.fargo} onChange={(e) => setPlayer(i, "fargo", e.target.value)} />
                    <input data-testid={`bc-player-robustness-${i}`} className={pInput + " w-28 shrink-0"} style={inputStyle} placeholder="Robustness" value={p.robustness} onChange={(e) => setPlayer(i, "robustness", e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
            <Field label="Comment / message (optional)">
              <textarea data-testid="bc-comment" rows={3} className={inputCls + " resize-y"} style={inputStyle} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Anything you'd like us to know — availability, preferred dates, etc." />
            </Field>
            <button type="submit" disabled={busy} data-testid="bc-submit-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 font-bold rounded-sm transition-colors disabled:opacity-60"
              style={{ background: C.gold, color: C.bg }}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {busy ? "Submitting…" : "Submit Challenge"}
            </button>
          </form>
        </section>

        {/* CONTACT */}
        <section id="bc-contact" className="py-20 sm:py-28" style={{ background: `linear-gradient(${C.greenDark}, ${C.bg})` }}>
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <Trophy className="h-12 w-12 mx-auto mb-5" style={{ color: C.gold }} />
            <h2 className="text-3xl sm:text-4xl font-medium mb-4" style={{ fontFamily: SERIF, color: C.goldLight }}>Questions about the Cup?</h2>
            {d.contact_note && <p className="mb-8" style={{ color: C.sub }}>{d.contact_note}</p>}
            <div className="flex flex-wrap justify-center gap-4">
              {d.contact_email && (
                <a href={`mailto:${d.contact_email}`} data-testid="braggers-contact-email" className="inline-flex items-center gap-2 px-6 py-3 rounded-sm font-semibold border transition-colors" style={{ borderColor: C.gold, color: C.gold }}>
                  <Mail className="h-4 w-4" /> {d.contact_email}
                </a>
              )}
              {d.contact_phone && (
                <a href={`tel:${d.contact_phone}`} data-testid="braggers-contact-phone" className="inline-flex items-center gap-2 px-6 py-3 rounded-sm font-semibold border transition-colors" style={{ borderColor: C.gold, color: C.gold }}>
                  <Phone className="h-4 w-4" /> {d.contact_phone}
                </a>
              )}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium mb-1.5" style={{ color: C.sub }}>{label}</span>
      {children}
    </label>
  );
}

function RosterCard({ title, players, highlight }) {
  return (
    <div className="rounded-sm border p-4" style={{ background: C.bg, borderColor: highlight ? C.gold : C.border }}>
      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: highlight ? C.gold : C.silver }}>{title}</p>
      <ul className="space-y-1">
        {(players || []).map((p, i) => (
          <li key={i} className="text-sm flex justify-between gap-2" style={{ color: C.text }}>
            <span>{typeof p === "string" ? p : p.name}</span>
            {typeof p === "object" && p.fargo && <span style={{ color: C.silver }}>{p.fargo}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
